import { useState, useCallback } from "react"
import { ButtonPlus } from "components/ui/buttons/ButtonPlus/ButtonPlus"
import { SelectCombination } from "../SelectCombination/SelectCombination"
import { useCompositeCategoryStore } from "store/compositeCategory.store"
import { useMessage } from "hooks/message.hook"
import { Tooltip } from "components/ui/tooltip/Tooltip"
import type { ICategoriesData } from "types/configuration.type"
import style from "./CombinationChildren.module.scss"

const { container, rowButton, row, plusButton, cell, addRowButtonWrapper, addColumnButtonWrapper } = style

type Props = {
	categories: ICategoriesData
	combinationId: string
}

export const CombinationChildren = ({ categories, combinationId }: Props) => {
	const { category, updateVariant } = useCompositeCategoryStore()
	const { error: errorMessage } = useMessage()

	// Keep track of the next unique key to use
	const [nextKeyId, setNextKeyId] = useState(1)

	// Get current variant for this combination or initialize with default structure
	const currentVariant = category.variants[combinationId] || [[]]
	const [combinations, setCombinations] = useState(() => {
		// If currentVariant is empty or has empty rows, initialize with one empty selector
		if (currentVariant.length === 0 || (currentVariant.length === 1 && currentVariant[0].length === 0)) {
			setNextKeyId(2) // Next key will be 2
			return [[{ key: "key-1", value: "", label: "" }]]
		}

		let maxKeyId = 0
		const result = currentVariant.map((row, rowIndex) =>
			row.map((categoryId, colIndex) => {
				const keyId = rowIndex * 10 + colIndex + 1 // Generate based on position
				maxKeyId = Math.max(maxKeyId, keyId)
				return {
					key: `key-${keyId}`,
					value: categoryId.toString(),
					label: `Category ${categoryId}`,
				}
			}),
		)
		setNextKeyId(maxKeyId + 1)
		return result
	})

	// Update the store whenever combinations change
	const updateStoreVariant = useCallback(
		(newCombinations: typeof combinations) => {
			const variant = newCombinations
				.map((row) => row.map((item) => parseInt(item.value)).filter((id) => !isNaN(id)))
				.filter((row) => row.length > 0)
			updateVariant(combinationId, variant)
		},
		[combinationId, updateVariant],
	)

	// Function to add a new column to a specific row
	const addColumnHandle = (rowIndex: number) => {
		setCombinations((prev) => {
			const newCombinations = [...prev]
			const currentRow = newCombinations[rowIndex]
			const newCombinationsUpdated = [...prev]
			newCombinationsUpdated[rowIndex] = [...currentRow, { key: `key-${nextKeyId}`, value: "", label: "" }]
			updateStoreVariant(newCombinationsUpdated)
			return newCombinationsUpdated
		})
		setNextKeyId((prev) => prev + 1) // Increment the key counter
	}

	// Function to add a new row with one cell
	const addRowHandle = () => {
		setCombinations((prev) => {
			const newCombinations = [...prev, [{ key: `key-${nextKeyId}`, value: "", label: "" }]]
			updateStoreVariant(newCombinations)
			return newCombinations
		})
		setNextKeyId((prev) => prev + 1) // Increment the key counter
	}

	const deleteColumnHandle = (rowIndex: number, columnIndex: number) => {
		setCombinations((prev) => {
			const newCombinations = [...prev]
			const currentRow = newCombinations[rowIndex]

			// Check if this would result in an empty grid (last column in last row when only one row exists)
			if (newCombinations.length === 1 && currentRow.length === 1) {
				errorMessage("Cannot delete the last remaining dropdown. At least one dropdown must exist.")
				return prev // Return unchanged state
			}

			// If this is the last column in the row, delete the entire row
			if (currentRow.length === 1) {
				newCombinations.splice(rowIndex, 1)
			} else {
				// Remove the specific column from the row
				newCombinations[rowIndex] = currentRow.filter((_, index) => index !== columnIndex)
			}

			updateStoreVariant(newCombinations)
			return newCombinations
		})
	}

	return (
		<div>
			<div className={container}>
				{combinations.map((combination, rowIndex) => (
					<div key={combination.map((item) => item.key).join("-")} className={row}>
						{combination.map((item) => (
							<div key={item.key} className={cell}>
								<SelectCombination
									value={item.value}
									onChange={(option) => {
										setCombinations((prev) => {
											const newCombinations = [...prev]
											const itemToUpdate = newCombinations[rowIndex].find((i) => i.key === item.key)
											if (itemToUpdate) {
												itemToUpdate.value = option.value
												itemToUpdate.label = option.label
											}
											updateStoreVariant(newCombinations)
											return newCombinations
										})
									}}
									deleteColumnHandle={() => {
										const columnIndex = combination.findIndex((i) => i.key === item.key)
										deleteColumnHandle(rowIndex, columnIndex)
									}}
									categories={categories}
								/>
							</div>
						))}
						<Tooltip text={combination.length > 3 ? "Maximum number of objects reached" : null} placement="bottom">
							<div className={addColumnButtonWrapper}>
								<ButtonPlus
									type="button"
									className={plusButton}
									onClick={() => addColumnHandle(rowIndex)}
									active={combination.length < 4} // Disable button if row has 4 or more columns
								/>
							</div>
						</Tooltip>
					</div>
				))}
			</div>
			<Tooltip text={combinations.length > 4 ? "Maximum number of rows reached" : null} placement="bottom">
				<div className={addRowButtonWrapper}>
					<ButtonPlus type="button" onClick={addRowHandle} className={rowButton} active={combinations.length < 5}>
						Add row
					</ButtonPlus>
				</div>
			</Tooltip>
		</div>
	)
}
